import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useUser } from "../contexts/UserContext";
import {
  BillingPlan,
  BillingCheckoutPreviewResult,
  BillingUser,
  PlanStatus,
  SELECTED_PLAN_STORAGE_KEY,
  SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY,
  createBillingCheckoutSession,
  evaluateBillingPlanChange,
  formatBillingProviderLabel,
  getBillingLifecycleState,
  getUser,
  isFreePlan,
  isLifetimePaidPlan,
  isPaidPlan,
  listBillingPlans,
  logBillingEvent,
  previewBillingCheckout,
} from "../utilities/billing";
import { capitalizeFirst, formatCurrency } from "../utilities/utilities";
import { getLocalizedPlan } from "../utilities/publicContent";
import "../styleSheets/plans.css";
import "../styleSheets/buttons.css";
import { queryKeys } from "../graphql/queryKeys";
import { useQueryClient } from "@tanstack/react-query";

const getPlanStatusMessages = (t: TFunction): Record<PlanStatus, string> => ({
  ACTIVE: t("checkout.plans.active"),
  PAYMENT_PENDING: t("checkout.plans.paymentPending"),
  ACTION_REQUIRED: t("checkout.plans.actionRequired"),
  PAYMENT_FAILED: t("checkout.plans.paymentFailed"),
  CANCELED: t("checkout.plans.canceled"),
});

const getPlanId = (plan?: BillingPlan | null): string =>
  (plan?.planId ?? plan?.id ?? "").trim();

const formatDateTime = (value?: string | null): string | null => {
  const normalizedValue = (value ?? "").trim();
  if (!normalizedValue) return null;

  const parsed = new Date(normalizedValue);
  return Number.isNaN(parsed.getTime())
    ? normalizedValue
    : parsed.toLocaleString();
};

const calculateAmount = (input: {
  selectedPlanPrice: number;
  currentPlanPrice: number;
  currentPlanRenewalDate: Date;
  today?: Date;
}): number => {
  const msPerDay = 1000 * 60 * 60 * 24;
  const rawDaysLeft = Math.floor(
    (input.currentPlanRenewalDate.getTime() -
      (input.today ?? new Date()).getTime()) /
      msPerDay,
  );

  const effectiveDaysLeft = Math.max(0, Math.min(365, rawDaysLeft));
  const proratedAmount = (input.currentPlanPrice / 365) * effectiveDaysLeft;

  return input.selectedPlanPrice - 0.85 * proratedAmount;
};

function Checkout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useUser();
  const queryClient = useQueryClient();
  const { t } = useTranslation(["common", "plans"]);
  const PLAN_STATUS_MESSAGES = getPlanStatusMessages(t);

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [userRecord, setUserRecord] = useState<BillingUser | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSettlingAfterReturn, setIsSettlingAfterReturn] = useState(false);
  // The return-from-provider poll ran out before the webhook settled the account. The user
  // may well have just paid, so the pre-payment screen (with an enabled "Pay now") must not
  // come back - see the dedicated render branch below.
  const [settleTimedOut, setSettleTimedOut] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [checkoutPreview, setCheckoutPreview] =
    useState<BillingCheckoutPreviewResult | null>(null);

  const planFromQuery = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("planId") ?? "").trim();
  }, [location.search]);

  const checkoutResult = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return (params.get("result") ?? "").trim().toLowerCase();
  }, [location.search]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => getPlanId(plan) === selectedPlanId) ?? null,
    [plans, selectedPlanId],
  );

  const currentPlan = useMemo(
    () =>
      plans.find(
        (plan) => getPlanId(plan) === (userRecord?.userPlanId ?? ""),
      ) ?? null,
    [plans, userRecord?.userPlanId],
  );
  const scheduledPlan = useMemo(
    () =>
      plans.find(
        (plan) => getPlanId(plan) === (userRecord?.scheduledPlanId ?? ""),
      ) ?? null,
    [plans, userRecord?.scheduledPlanId],
  );
  const localizedSelectedPlan = useMemo(
    () => (selectedPlan ? getLocalizedPlan(selectedPlan, t) : null),
    [selectedPlan, t],
  );

  const localizedCurrentPlan = useMemo(
    () => (currentPlan ? getLocalizedPlan(currentPlan, t) : null),
    [currentPlan, t],
  );
  const localizedScheduledPlan = useMemo(
    () => (scheduledPlan ? getLocalizedPlan(scheduledPlan, t) : null),
    [scheduledPlan, t],
  );

  const selectedPlanChange = useMemo(
    () =>
      evaluateBillingPlanChange({
        user: userRecord,
        currentPlan,
        targetPlan: selectedPlan,
      }),
    [currentPlan, selectedPlan, userRecord],
  );
  const billingState = useMemo(
    () => getBillingLifecycleState(userRecord),
    [userRecord],
  );
  const effectiveAtLabel = useMemo(
    () => formatDateTime(billingState.billingEffectiveAt),
    [billingState.billingEffectiveAt],
  );

  const selectedPlanIsValid =
    !!selectedPlan && selectedPlanChange.kind !== "invalid";
  const selectedPlanRequiresCheckout = selectedPlanChange.kind === "checkout";
  const selectedPlanSchedulesDowngrade =
    selectedPlanChange.kind === "scheduled_downgrade";
  const selectedPlanAlreadyScheduled =
    selectedPlanSchedulesDowngrade && !!selectedPlanChange.alreadyScheduled;
  const currentPlanId = getPlanId(currentPlan);
  const selectedPlanPrice = Number(selectedPlan?.price ?? 0);
  const currentPlanPrice = Number(currentPlan?.price ?? 0);
  const selectedPlanIsDifferent =
    !!selectedPlan && getPlanId(selectedPlan) !== currentPlanId;
  const currentPlanIsLifetimePaid = isLifetimePaidPlan(currentPlan);
  const currentPlanIsFree = !currentPlan || isFreePlan(currentPlan);
  const selectedPlanHasPositivePrice = selectedPlanPrice > 0;
  const showLifetimeRestriction =
    !!selectedPlan && selectedPlanIsDifferent && currentPlanIsLifetimePaid;
  const selectedPlanCouldRequirePaidCheckout =
    !!selectedPlan &&
    selectedPlanIsDifferent &&
    selectedPlanHasPositivePrice &&
    !selectedPlanSchedulesDowngrade &&
    !showLifetimeRestriction &&
    (
      selectedPlanRequiresCheckout ||
      currentPlanIsFree ||
      selectedPlanPrice > currentPlanPrice
    );
  const showUpgradeCheckout = selectedPlanCouldRequirePaidCheckout;
  const showScheduledDowngradeCheckout =
    selectedPlanIsValid &&
    selectedPlanSchedulesDowngrade &&
    !showLifetimeRestriction;
  const hasPreviewedUpgradeCheckout = !!checkoutPreview;
  const canProceedWithCheckout =
    !!selectedPlan &&
    !isSubmitting &&
    (selectedPlanIsValid || hasPreviewedUpgradeCheckout || showUpgradeCheckout);
  const canDeferCheckout =
    !!selectedPlan &&
    !isSubmitting &&
    (selectedPlanRequiresCheckout || hasPreviewedUpgradeCheckout || showUpgradeCheckout);
  const hasPersistedPlanChange =
    !!(userRecord?.pendingPlanId ?? "").trim() ||
    !!(userRecord?.scheduledPlanId ?? "").trim();
  const isPaidToPaidUpgrade =
    !!selectedPlan &&
    !!currentPlan &&
    !isFreePlan(currentPlan) &&
    selectedPlanHasPositivePrice &&
    currentPlanPrice > 0 &&
    selectedPlanPrice > currentPlanPrice &&
    !showLifetimeRestriction;

  useEffect(() => {
    if (!selectedPlanCouldRequirePaidCheckout || !selectedPlan) {
      setCheckoutPreview(null);
      return;
    }

    let canceled = false;
    const nextPlanId = getPlanId(selectedPlan);

    if (!nextPlanId) {
      setCheckoutPreview(null);
      return;
    }

    setCheckoutPreview(null);

    void previewBillingCheckout({ planId: nextPlanId })
      .then((preview) => {
        if (canceled) return;
        setCheckoutPreview(preview);
      })
      .catch(() => {
        if (canceled) return;
      });

    return () => {
      canceled = true;
    };
  }, [selectedPlan, selectedPlanCouldRequirePaidCheckout]);

  useEffect(() => {
    const load = async () => {
      if (!isAuthenticated || !user?.userId) {
        setError(t("checkout.errorMessage.mustSignIn"));
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const [planList, currentUser] = await Promise.all([
          listBillingPlans(),
          getUser(user.userId),
        ]);
        setPlans(planList);
        setUserRecord(currentUser);

        const storedPlanId = (
          window.localStorage.getItem(SELECTED_PLAN_STORAGE_KEY) ?? ""
        ).trim();
        const fallbackPending = (currentUser?.pendingPlanId ?? "").trim();
        const effectivePlanId =
          planFromQuery || fallbackPending || storedPlanId;
        setSelectedPlanId(effectivePlanId);
        setInfoMessage(null);
      } catch (e: any) {
        setError(e?.message ?? t("checkout.errorMessage.unableLoadDetails"));
      } finally {
        setIsLoading(false);
      }
    };

    void load();
  }, [isAuthenticated, planFromQuery, t, user?.userId]);

  const refreshUser = async () => {
    if (!user?.userId) return null;
    const fresh = await getUser(user.userId);
    setUserRecord(fresh);
    return fresh;
  };

  const invalidatePlanCache = async () => {
    if (!user?.userId) return;
    queryClient.removeQueries({ queryKey: queryKeys.plan(user.userId) });
  };

  const settleCheckoutReturn = async () => {
    if (!user?.userId) return;

    setIsSettlingAfterReturn(true);
    setError(null);

    try {
      for (let attempt = 0; attempt < 12; attempt += 1) {
        const fresh = await refreshUser();
        const status = String(fresh?.planStatus ?? "").toUpperCase();
        const hasSettled =
          status === "ACTIVE" ||
          status === "PAYMENT_FAILED" ||
          status === "ACTION_REQUIRED" ||
          status === "CANCELED";

        if (hasSettled) {
          await invalidatePlanCache();
          const providerLabel = formatBillingProviderLabel(
            fresh?.billingProvider ?? userRecord?.billingProvider ?? null,
          );
          const selectedPlanHasRecurringBilling =
            !!selectedPlan &&
            String(selectedPlan.billingCycle ?? "")
              .trim()
              .toLowerCase() !== "no billing";

          const returnMessage =
            status === "ACTIVE"
              ? selectedPlanHasRecurringBilling && providerLabel
                ? t("checkout.onScreenMessage.paymentProcessedWithProvider", {
                    provider: providerLabel,
                  })
                : t("checkout.onScreenMessage.paymentProcessed")
              : t("checkout.onScreenMessage.paymentCouldNotBeProcessed");

          if (status === "ACTIVE") {
            window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
          }

          setInfoMessage(returnMessage);
          setIsSettlingAfterReturn(false);
          await new Promise((resolve) => setTimeout(resolve, 1500));
          navigate("/Dashboard");
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 1200));
      }

      setSettleTimedOut(true);
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableRefreshState"));
    } finally {
      setIsSettlingAfterReturn(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !user?.userId) return;
    if (checkoutResult !== "success") return;
    void settleCheckoutReturn();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutResult, isAuthenticated, user?.userId]);

  const handleProceedToPayment = async () => {
    if (!user?.userId || !selectedPlan || !canProceedWithCheckout) return;

    const pendingPlanId = getPlanId(selectedPlan);
    if (!pendingPlanId) {
      setError(t("checkout.errorMessage.invalidSelectedPlan"));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setInfoMessage(null);

      const session = await createBillingCheckoutSession({
        planId: pendingPlanId,
      });

      if (session.action === "scheduled_downgrade") {
        window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
        await refreshUser();
        await invalidatePlanCache();
        navigate("/Dashboard");
        return;
      }

      if (session.action !== "checkout") {
        setError(t("checkout.errorMessage.expectedCheckoutSession"));
        return;
      }

      await logBillingEvent({
        userId: user.userId,
        eventType: "CHECKOUT_STARTED",
        provider:
          session.billingProvider ??
          userRecord?.billingProvider ??
          "billing-provider",
        providerResourceRef: session.checkoutSessionRef,
        metadata: {
          planId: pendingPlanId,
          fromPlanId: userRecord?.userPlanId ?? "",
          toPlanId: pendingPlanId,
          sessionRef: session.checkoutSessionRef,
          billingProviderId: session.billingProviderId ?? null,
          startedAt: new Date().toISOString(),
        },
        status: "PENDING",
      });

      await invalidatePlanCache();
      window.location.assign(session.checkoutUrl);
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableProcessCheckout"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePayLater = async () => {
    if (!user?.userId || !selectedPlan || !canDeferCheckout) {
      return;
    }

    const pendingPlanId = getPlanId(selectedPlan);
    if (!pendingPlanId) {
      setError(t("checkout.errorMessage.invalidSelectedPlan"));
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setInfoMessage(null);

      const result = await createBillingCheckoutSession({
        planId: pendingPlanId,
        action: "defer_checkout",
      });

      if (result.action !== "defer_checkout") {
        setError(t("checkout.errorMessage.expectedDeferredCheckout"));
        return;
      }

      window.localStorage.setItem(SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY, "true");
      await refreshUser();
      await invalidatePlanCache();

      await logBillingEvent({
        userId: user.userId,
        eventType: "CHECKOUT_PAY_LATER",
        provider:
          result.billingProvider ??
          userRecord?.billingProvider ??
          "billing-provider",
        providerResourceRef: userRecord?.checkoutSessionRef ?? undefined,
        amount: result.pendingPaymentAmount,
        metadata: {
          planId: pendingPlanId,
          sessionRef: userRecord?.checkoutSessionRef ?? null,
          clickedAt: new Date().toISOString(),
        },
        status: "SKIPPED",
      });

      navigate("/Dashboard");
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableDeferCheckout"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSeePlans = async () => {
    if (isSubmitting) return;

    if (!user?.userId || !selectedPlanRequiresCheckout || !selectedPlan) {
      navigate("/Plans");
      return;
    }

    const pendingPlanId = getPlanId(selectedPlan);
    if (!pendingPlanId) {
      navigate("/Plans");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setInfoMessage(null);

      const result = await createBillingCheckoutSession({
        planId: pendingPlanId,
        action: "defer_checkout",
      });

      if (result.action !== "defer_checkout") {
        setError(t("checkout.errorMessage.expectedDeferredCheckout"));
        return;
      }

      window.localStorage.setItem(SKIP_CHECKOUT_UNTIL_NEXT_LOGIN_KEY, "true");
      await refreshUser();
      await invalidatePlanCache();
      navigate("/Plans");
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableReturnToPlans"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelSelection = async () => {
    if (isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError(null);
      setInfoMessage(null);

      const cancelPlanId = currentPlanId || getPlanId(selectedPlan);

      if (cancelPlanId && hasPersistedPlanChange) {
        const result = await createBillingCheckoutSession({
          planId: cancelPlanId,
          action: "cancel_plan_change",
        });

        if (result.action !== "cancel_plan_change") {
          setError(t("checkout.errorMessage.expectedCanceledPlanChange"));
          return;
        }

        await refreshUser();
        await invalidatePlanCache();
      }

      window.localStorage.removeItem(SELECTED_PLAN_STORAGE_KEY);
      setSelectedPlanId("");
      setCheckoutPreview(null);
      navigate("/Dashboard");
    } catch (e: any) {
      setError(e?.message ?? t("checkout.errorMessage.unableCancelSelection"));
    } finally {
      setIsSubmitting(false);
    }
  };

  const normalizedStatus = billingState.normalizedStatus;
  const currentPlanDisplayName =
    localizedCurrentPlan?.displayName ??
    currentPlan?.name ??
    userRecord?.userPlanId ??
    "";
  const scheduledPlanDisplayName =
    localizedScheduledPlan?.displayName ??
    scheduledPlan?.name ??
    userRecord?.scheduledPlanId ??
    "";
  const selectedPlanDisplayName =
    localizedSelectedPlan?.displayName ?? selectedPlan?.name ?? selectedPlanId;
  const selectedPlanPriceLabel =
    selectedPlan && Number.isFinite(selectedPlanPrice)
      ? formatCurrency(selectedPlanPrice)
      : t("checkout.labels.amountUnavailable");
  const frontendEstimatedUpgradeAmount = useMemo(() => {
    if (!isPaidToPaidUpgrade || !billingState.billingEffectiveAt) {
      return null;
    }

    const currentPlanRenewalDate = new Date(billingState.billingEffectiveAt);
    if (Number.isNaN(currentPlanRenewalDate.getTime())) {
      return null;
    }

    return calculateAmount({
      selectedPlanPrice,
      currentPlanPrice,
      currentPlanRenewalDate,
    });
  }, [
    billingState.billingEffectiveAt,
    currentPlanPrice,
    isPaidToPaidUpgrade,
    selectedPlanPrice,
  ]);
  const upgradeCreditLabel =
    typeof checkoutPreview?.upgradeCreditAmount === "number" && typeof checkoutPreview?.upgradeFeeAmount === "number"
      ? (formatCurrency(checkoutPreview.upgradeCreditAmount + checkoutPreview.upgradeFeeAmount))
      : t("checkout.labels.amountUnavailable");
  const upgradeFeeLabel =
    typeof checkoutPreview?.upgradeFeeAmount === "number"
      ? formatCurrency(checkoutPreview.upgradeFeeAmount)
      : t("checkout.labels.amountUnavailable");
  const amountDueNowValue = showLifetimeRestriction
    ? 0
    : showScheduledDowngradeCheckout
      ? 0
      : typeof checkoutPreview?.estimatedPendingAmount === "number"
        ? checkoutPreview.estimatedPendingAmount
        : (frontendEstimatedUpgradeAmount ?? selectedPlanPrice);
  const amountDueNowLabel = Number.isFinite(amountDueNowValue)
    ? formatCurrency(amountDueNowValue)
    : t("checkout.labels.amountUnavailable");
  const showEstimatedAmountNote =
    showUpgradeCheckout &&
    isPaidToPaidUpgrade &&
    Number.isFinite(amountDueNowValue);
  const showUpgradeBreakdown =
    checkoutPreview?.activationFlow === "paid_upgrade";
  const activePlanChangeMode =
    showUpgradeCheckout ||
    showScheduledDowngradeCheckout ||
    showLifetimeRestriction;
  const scheduledDowngradeMessage = showScheduledDowngradeCheckout
    ? isPaidPlan(selectedPlan)
      ? t("checkout.onScreenMessage.paidDowngradeConfirmation", {
          currentPlanName: currentPlanDisplayName,
          selectedPlanName: selectedPlanDisplayName,
          dateTime: effectiveAtLabel ?? "",
        })
      : t("checkout.onScreenMessage.freeDowngradeConfirmation", {
          currentPlanName: currentPlanDisplayName,
          selectedPlanName: selectedPlanDisplayName,
          dateTime: effectiveAtLabel ?? "",
        })
    : null;
  const billingMessages = useMemo(() => {
    const messages: string[] = [];

    if (checkoutResult === "cancel") {
      messages.push(t("checkout.onScreenMessage.checkoutCanceled"));
    }

    if (selectedPlanAlreadyScheduled && effectiveAtLabel) {
      messages.push(
        t("checkout.onScreenMessage.downgradeAlreadyScheduled", {
          dateTime: effectiveAtLabel,
        }),
      );
    } else if (
      selectedPlanSchedulesDowngrade &&
      !billingState.hasScheduledDowngrade &&
      effectiveAtLabel
    ) {
      messages.push(
        t("checkout.onScreenMessage.planTakesEffectOn", {
          dateTime: effectiveAtLabel,
        }),
      );
    }

    if (billingState.hasScheduledDowngrade && scheduledPlanDisplayName) {
      messages.push(
        effectiveAtLabel
          ? t("checkout.onScreenMessage.scheduledChangeOn", {
              planName: scheduledPlanDisplayName,
              dateTime: effectiveAtLabel,
            })
          : t("checkout.onScreenMessage.scheduledChangeAtPeriodEnd", {
              planName: scheduledPlanDisplayName,
            }),
      );
    }

    if (billingState.hasPaymentFailure && !billingState.hasPendingCheckout) {
      messages.push(
        effectiveAtLabel
          ? t("checkout.onScreenMessage.paymentFailedUntil", {
              dateTime: effectiveAtLabel,
            })
          : t("checkout.onScreenMessage.paymentFailedChoosePlan"),
      );
    }

    if (
      billingState.isCanceledAtPeriodEnd &&
      !billingState.hasScheduledDowngrade
    ) {
      messages.push(
        effectiveAtLabel
          ? t("checkout.onScreenMessage.subscriptionCanceledUntil", {
              dateTime: effectiveAtLabel,
            })
          : t("checkout.onScreenMessage.subscriptionCanceled"),
      );
    }

    return messages;
  }, [
    billingState.hasPendingCheckout,
    billingState.hasPaymentFailure,
    billingState.hasScheduledDowngrade,
    billingState.isCanceledAtPeriodEnd,
    checkoutResult,
    effectiveAtLabel,
    scheduledPlanDisplayName,
    selectedPlanAlreadyScheduled,
    selectedPlanSchedulesDowngrade,
    t,
  ]);
  const showBillingSummary =
    !!currentPlan ||
    !!scheduledPlan ||
    billingMessages.length > 0 ||
    !!infoMessage;

  if (isLoading || isSettlingAfterReturn) {
    return (
      <div className="section-plans">
        <div className="plans-container">
          <div className="plans-title">
            {capitalizeFirst(t("checkout.checkout"))}
          </div>
          <p>{t("checkout.loading")}</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="section-plans">
        <div className="plans-container">
          <div className="plans-title">
            {capitalizeFirst(t("checkout.checkout"))}
          </div>
          <p>{t("common.signIn")}</p>
          <Link className="btn btn--white btn--size-md" to="/Register">
            {t("header.menu.signInRegister")}
          </Link>
        </div>
      </div>
    );
  }

  if (settleTimedOut) {
    return (
      <div className="section-plans">
        <div className="plans-container">
          <div className="plans-title">
            {capitalizeFirst(t("checkout.checkout"))}
          </div>
          <p role="status">
            {t("checkout.onScreenMessage.paymentStillConfirming")}
          </p>
          <Link className="btn btn--white btn--size-md" to="/Dashboard">
            {t("header.menu.dashboard")}
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="section-plans">
        <div className="plans-container">
          <div className="plans-title">
            {capitalizeFirst(t("checkout.checkout"))}
          </div>
          <p className="plans-error">{error}</p>
          <Link className="btn btn--white btn--size-md" to="/Plans">
            {t("checkout.backPlans")}
          </Link>
        </div>
      </div>
    );
  }

  if (!selectedPlanIsValid && !showBillingSummary) {
    return (
      <div className="section-plans">
        <div className="plans-container">
          <div className="plans-title">
            {capitalizeFirst(t("checkout.checkout"))}
          </div>
          <p>{t("checkout.onScreenMessage.planUnavailable")}</p>
          <Link className="btn btn--white btn--size-md" to="/Plans">
            {t("checkout.onScreenMessage.goToPlans")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="section-plans">
      <div className="plans-container">
        <div className="plans-title">
          {capitalizeFirst(t("checkout.checkout"))}
        </div>

        <div
          className="plan-card"
          style={{ width: "100%", maxWidth: 720, minHeight: 0 }}
        >
          <h3 className="plan-name">
            {activePlanChangeMode || selectedPlanIsValid
              ? `${t("checkout.selected")}: ${selectedPlanDisplayName}`
              : t("checkout.labels.currentBillingStatus")}
          </h3>
          <p className="plan-description">
            {showUpgradeCheckout
              ? t("checkout.labels.upgradeSummaryDescription")
              : showScheduledDowngradeCheckout || showLifetimeRestriction
                ? (localizedSelectedPlan?.displayDescription ?? "")
                : selectedPlanIsValid
                  ? (localizedSelectedPlan?.displayDescription ?? "")
                  : t("checkout.labels.currentBillingStateDescription")}
          </p>

          {activePlanChangeMode && selectedPlan ? (
            <>
              <p>
                <strong>{t("checkout.labels.currentPlan")}</strong>{" "}
                {currentPlanDisplayName ||
                  t("checkout.labels.amountUnavailable")}
              </p>
              <p>
                <strong>{t("checkout.status")}</strong>{" "}
                {PLAN_STATUS_MESSAGES[normalizedStatus] ??
                  t("checkout.labels.unknownPlanStatus")}
              </p>
              <p>
                <strong>{t("checkout.labels.selectedPlanPrice")}</strong>{" "}
                {selectedPlanPriceLabel}
              </p>
              {showUpgradeBreakdown ? (
                <>
                  <p>
                    <strong>{t("checkout.labels.proratedCredit")}</strong>{" "}
                    {upgradeCreditLabel}
                  </p>
                  <p>
                    <strong>{t("checkout.labels.changePlanFee")}</strong>{" "}
                    {upgradeFeeLabel}
                  </p>
                </>
              ) : null}
              <p>
                <strong>{t("checkout.labels.amountDueNow")}</strong>{" "}
                {amountDueNowLabel}
              </p>
              {showEstimatedAmountNote ? (
                <p>{t("checkout.onScreenMessage.estimatedAmountNote")}</p>
              ) : null}
              {showScheduledDowngradeCheckout && scheduledDowngradeMessage ? (
                <p>{scheduledDowngradeMessage}</p>
              ) : null}
              {showLifetimeRestriction ? (
                <p>
                  {t("checkout.onScreenMessage.lifetimeDowngradeRestriction")}
                </p>
              ) : null}

              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: "1px solid #ddd",
                }}
              />
            </>
          ) : selectedPlanIsValid && selectedPlan ? (
            <>
              <div className="plan-price">
                {formatCurrency(Number(selectedPlan.price ?? 0))}
              </div>
              <div className="plan-cycle">
                {localizedSelectedPlan?.displayBillingCycle ??
                  selectedPlan.billingCycle}
              </div>

              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: "1px solid #ddd",
                }}
              />
            </>
          ) : null}

          {!activePlanChangeMode && currentPlanDisplayName ? (
            <p>
              <strong>{t("checkout.labels.currentPlan")}</strong>{" "}
              {currentPlanDisplayName}
            </p>
          ) : null}

          {!activePlanChangeMode && scheduledPlanDisplayName ? (
            <p>
              <strong>{t("checkout.labels.scheduledPlan")}</strong>{" "}
              {scheduledPlanDisplayName}
            </p>
          ) : null}

          {!activePlanChangeMode ? (
            <p>
              <strong>{t("checkout.status")}</strong>{" "}
              {PLAN_STATUS_MESSAGES[normalizedStatus] ??
                t("checkout.labels.unknownPlanStatus")}
            </p>
          ) : null}

          {effectiveAtLabel && !activePlanChangeMode ? (
            <p>
              <strong>{t("checkout.labels.currentPeriodEnd")}</strong>{" "}
              {effectiveAtLabel}
            </p>
          ) : null}

          {infoMessage ? <p>{infoMessage}</p> : null}

          {!activePlanChangeMode
            ? billingMessages.map((message) => <p key={message}>{message}</p>)
            : null}

          <div
            style={{
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
              marginTop: 18,
            }}
          >
            {showUpgradeCheckout ? (
              <>
                <button
                  className="btn btn--green btn--size-md"
                  onClick={handleProceedToPayment}
                  disabled={!canProceedWithCheckout}
                >
                  {t("checkout.payNow")}
                </button>
                <button
                  className="btn btn--white btn--size-md"
                  onClick={handlePayLater}
                  disabled={!canDeferCheckout}
                >
                  {t("checkout.payLaterAction")}
                </button>
                <button
                  className="btn btn--white btn--size-md"
                  onClick={handleCancelSelection}
                  disabled={isSubmitting}
                >
                  {t("checkout.cancelCheckout")}
                </button>
              </>
            ) : showScheduledDowngradeCheckout ? (
              <>
                <button
                  className="btn btn--green btn--size-md"
                  onClick={handleProceedToPayment}
                  disabled={isSubmitting || selectedPlanAlreadyScheduled}
                >
                  {t("checkout.confirmDowngrade")}
                </button>
                <button
                  className="btn btn--white btn--size-md"
                  onClick={handleCancelSelection}
                  disabled={isSubmitting}
                >
                  {t("checkout.cancelCheckout")}
                </button>
              </>
            ) : showLifetimeRestriction ? (
              <button
                className="btn btn--white btn--size-md"
                onClick={handleCancelSelection}
                disabled={isSubmitting}
              >
                {t("checkout.cancelCheckout")}
              </button>
            ) : null}

            {!activePlanChangeMode ? (
              <button
                className="btn btn--white btn--size-md"
                onClick={handleSeePlans}
                disabled={isSubmitting}
              >
                {t("checkout.plans.change")}
              </button>
            ) : null}
          </div>

          {selectedPlanIsValid &&
          !activePlanChangeMode &&
          !isPaidPlan(selectedPlan) ? (
            <p style={{ marginTop: 10, color: "#8a1f12" }}>
              {t("checkout.onScreenMessage.planUnavailable")}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export default Checkout;
